import { CameraIcon } from '@curology/ui-components-web/react/Icon/icons/CameraIcon';
import { FaceIcon } from '@curology/ui-components-web/react/Icon/icons/FaceIcon';
import { FrontFacingSelfieIcon } from '@curology/ui-components-web/react/Icon/icons/FrontFacingSelfieIcon';
import { LeftFacingSelfieIcon } from '@curology/ui-components-web/react/Icon/icons/LeftFacingSelfieIcon';
import { NeckIcon } from '@curology/ui-components-web/react/Icon/icons/NeckIcon';
import { RightFacingSelfieIcon } from '@curology/ui-components-web/react/Icon/icons/RightFacingSelfieIcon';

import { type Brand } from '@/consts/brands';
import { type TreatmentCategory } from '@/consts/treatmentCategories';
import { type FlowType } from '@/services/funnelService/consts/flowType';
import { type PhotoResource } from '@/services/funnelService/types';
import {
  PhotoUploadService,
  type Photo,
} from '@/services/photoUploadService/client';
import { UserService } from '@/services/userService/client';
import { type AnalyticsPatientResource } from '@/types/metrics/analyticsPatient';

import { type QuestionPhotoUploadModel } from '../../../config/questions/photoUploadQuestion';
import { type PhotoUploadFile } from '../types/types';

const getApplicablePhotos = (
  question: QuestionPhotoUploadModel,
  photoTypes?: string[],
) => {
  const photos: PhotoResource[] = [];

  if (photoTypes) {
    const questionPhotos = question.photoData;

    photoTypes.forEach((photoType: string) => {
      questionPhotos.forEach((questionPhoto: PhotoResource) => {
        if (questionPhoto.type === photoType) {
          photos.push(questionPhoto);
        }
      });
    });
  }

  return photos.sort(
    (currentPhoto: PhotoResource, nextPhoto: PhotoResource) => {
      const currentPhotoDisplayOrder = currentPhoto.displayOrder || 0;
      const nextPhotoDisplayOrder = nextPhoto.displayOrder || 0;

      return currentPhotoDisplayOrder - nextPhotoDisplayOrder;
    },
  );
};

export const getPhotoUploaderIcon = (photo: PhotoResource) => {
  switch (photo.uploadImagePlaceholderIcon) {
    case 'camera':
      return <CameraIcon />;
    case 'frontFacingSelfie':
      return <FrontFacingSelfieIcon />;
    case 'leftFacingSelfie':
      return <LeftFacingSelfieIcon />;
    case 'neck':
      return <NeckIcon />;
    case 'rightFacingSelfie':
      return <RightFacingSelfieIcon />;
    default:
      return <FaceIcon />;
  }
};

const alreadyHavePhotoByPerspective = (
  previouslyUploadedPhotos: PhotoUploadFile[],
  photoPerspective: string,
) => {
  for (let i = 0; i < previouslyUploadedPhotos.length; i++) {
    if (previouslyUploadedPhotos[i].photoType === photoPerspective) {
      return true;
    }
  }

  return false;
};

const getPreviouslyUploadedPhotos = (
  photos: PhotoResource[],
  uploadedPhotos: Photo[],
  treatmentCategory?: TreatmentCategory,
) => {
  const previouslyUploadedPhotos: PhotoUploadFile[] = [];

  if (photos && uploadedPhotos) {
    uploadedPhotos.reverse();

    uploadedPhotos.forEach((uploadedPhoto: Photo) => {
      const photoPerspective = uploadedPhoto.metadata.photo_perspective;
      const photoTreatmentCategory = uploadedPhoto.metadata.treatment_category;
      const photo = photos.find((photo) => photo.type === photoPerspective);

      if (
        photoPerspective &&
        photoTreatmentCategory &&
        photo &&
        photoTreatmentCategory === treatmentCategory &&
        (!alreadyHavePhotoByPerspective(
          previouslyUploadedPhotos,
          photoPerspective,
        ) ||
          photo.allowsMultiple)
      ) {
        previouslyUploadedPhotos.push({
          file: uploadedPhoto.localUrl,
          photoId: uploadedPhoto.id,
          photoType: photoPerspective,
        });
      }
    });
  }

  return previouslyUploadedPhotos;
};

export const getRequiredPhotoTypes = (photos: PhotoResource[]) => {
  return photos.filter((photo) => photo.required).map((photo) => photo.type);
};

export const initPageData = async (
  question: QuestionPhotoUploadModel,
  setPatient: React.Dispatch<
    React.SetStateAction<AnalyticsPatientResource | undefined>
  >,
  setPhotos: React.Dispatch<React.SetStateAction<PhotoResource[]>>,
  setShowWeAreSorryMessage: React.Dispatch<React.SetStateAction<boolean>>,
  setSubmitButtonDisabled: React.Dispatch<React.SetStateAction<boolean>>,
  setUploadedFiles: React.Dispatch<React.SetStateAction<PhotoUploadFile[]>>,
  brand: Brand,
  treatmentCategory?: TreatmentCategory,
  flowType?: FlowType,
) => {
  try {
    const [patient, photoTypes, uploadedPhotoData] = await Promise.all([
      UserService.getPatient(brand),
      PhotoUploadService.getPhotoTypes(
        brand,
        question.treatmentCategory,
        flowType,
      ),
      PhotoUploadService.getUploadedPhotoData(brand),
    ]);

    const photos = getApplicablePhotos(question, photoTypes);

    const previouslyUploadedPhotos = getPreviouslyUploadedPhotos(
      photos,
      uploadedPhotoData.photos,
      treatmentCategory,
    );

    const requiredPhotoTypes = getRequiredPhotoTypes(photos);

    setPatient(patient);
    setPhotos(photos);

    setSubmitButtonDisabled(
      shouldDisableSubmitButton(requiredPhotoTypes, previouslyUploadedPhotos),
    );

    setUploadedFiles(previouslyUploadedPhotos);

    return previouslyUploadedPhotos;
  } catch (exception) {
    setShowWeAreSorryMessage(true);
  }
};

export const shouldDisableSubmitButton = (
  requiredPhotoTypes: string[],
  files: PhotoUploadFile[],
) => {
  const uploadedFilePhotoTypes = files.map((file) => file.photoType);

  return !requiredPhotoTypes.every((requiredPhotoType) =>
    uploadedFilePhotoTypes.includes(requiredPhotoType),
  );
};

interface Errorlike {
  message: string;
}

export function cleanInvalidFileTypeMessage(input: Errorlike) {
  if (input.message.startsWith('File type must be')) {
    return new Error('Invalid filetype. Please use a jpg, heif, or png.');
  }
  return input;
}
